/**
 * On-demand Judge.me widget loading.
 *
 * Judge.me's loader is selector-gated: at docReady it asks whether any widget markup is on
 * the page and, finding none, loads nothing. Measured on the live store 2026-08-20 —
 * reviews section present = 18 requests / 215KB, absent = 1 request / 4KB — and that
 * difference is worth 467ms of TBT and 6 Lighthouse Performance points (n=5 per arm).
 *
 * sections/wild-reviews.liquid therefore parks the app block's markup in a <template>, whose
 * content lives in a DocumentFragment that document.querySelectorAll cannot descend into.
 * This injects it and re-runs the vendor loader when the section approaches the viewport.
 *
 * THE VENDOR CONTRACT, read from judgeme-716/assets/loader.js and verified live:
 *
 *     (function(){ if (window.jdgm = window.jdgm||{}, jdgm._loaderExecuted) return;
 *                  jdgm._loaderExecuted = true; ...
 *       const O = () => document.querySelectorAll(<every widget selector>).length > 0;
 *       jdgm.docReady(function(){ (window.jdgmLoadJS || O()) && loadBundles(); });
 *     })();
 *
 * So the loader is idempotent — re-adding the <script> alone does nothing. Clearing
 * `_loaderExecuted` first lets a second execution run its docReady path against the markup
 * we just injected. Confirmed against the live ?view=perftest page: 1 judge.me response
 * before, 13 after, stars rendering.
 *
 * `_loaderExecuted` is a PRIVATE vendor internal. If Judge.me renames it this silently stops
 * working — which is exactly why we verify the widget actually mounted and, if it didn't,
 * reveal the section's existing curated reviews rather than leave a gap. The failure mode is
 * "shopper sees our review cards instead of the widget", never "shopper sees nothing".
 */
(function () {
  var VERIFY_MS = 5000; // how long to give the widget to prove it mounted
  var started = false;

  function judgeRoot() {
    return document.querySelector('[data-pdp-judge]');
  }

  /* Counting .jdgm-rev nodes proves NOTHING: the app block ships a
     `display:none` .jdgm-legacy-widget-content blob that already contains .jdgm-rev and
     .jdgm-rev-widg, so a presence check passes even when the widget never mounted. Caught
     2026-08-20 by the simulated vendor-rename test — the check said "mounted", the fallback
     never fired, and the section collapsed to nothing. Require real rendered height. */
  function mounted() {
    var root = judgeRoot();
    return !!root && root.getBoundingClientRect().height > 40;
  }

  /* The section already ships a curated fallback for the "widget returned nothing" case
     (see the inline script in wild-reviews.liquid). Reuse it rather than inventing a
     second empty-state. */
  function fallback() {
    var judge = judgeRoot();
    var generic = document.querySelector('[data-pdp-generic]');
    if (!generic) return;
    generic.hidden = false;
    if (judge) judge.hidden = true;
  }

  function load() {
    if (started) return;
    started = true;

    var tpl = document.querySelector('template[data-jdgm-deferred]');
    var host = tpl && tpl.parentNode;
    if (!tpl || !host) return;

    host.appendChild(tpl.content.cloneNode(true));
    tpl.remove();

    var loaderScript = document.querySelector('script[src*="judgeme"][src*="loader.js"]');
    if (!loaderScript) { fallback(); return; }

    window.jdgmLoadJS = true;
    try {
      if (window.jdgm) window.jdgm._loaderExecuted = false;
    } catch (e) {
      /* Frozen or renamed by a vendor update — the verify timer below catches it. */
    }

    var s = document.createElement('script');
    s.src = loaderScript.src;
    s.onerror = fallback;
    document.head.appendChild(s);

    /* Release the section's own empty-widget check. It is gated on this event in on-demand
       mode: starting its 1.5s timer at page load would hide [data-pdp-judge] long before the
       shopper ever scrolled here — and a hidden element never intersects, so the widget
       would never load at all. */
    try {
      document.dispatchEvent(new CustomEvent('wp:reviews-widget-injected'));
    } catch (e) {}

    setTimeout(function () { if (!mounted()) fallback(); }, VERIFY_MS);
  }

  function wantsReviewsNow() {
    // Deep links must not wait for a scroll: our own #reviews anchor (the price block and
    // the sticky CTA both point at it) and Judge.me's own entry points.
    return /^#(reviews|judgeme)/.test(location.hash) || /judgeme_/.test(location.search);
  }

  function init() {
    var root = judgeRoot();
    if (!root || !document.querySelector('template[data-jdgm-deferred]')) return;

    if (wantsReviewsNow()) { load(); return; }

    if (!('IntersectionObserver' in window)) { load(); return; }

    /* Observe the SECTION, not [data-pdp-judge]. The judge div holds only an inert template
       at this point, and anything that hides it — the section's own empty-widget check, a
       future tweak — would stop a hidden element from ever intersecting and strand the
       widget unloaded. The section wrapper always has real layout. */
    var observed = root.closest('.wild-reviews') || root;

    /* Lead time is NOT enough on its own. Measured 2026-08-20 on the live PDP, this section
       sits only 988px below the fold on a 412x823 phone and 448px on a 1440x900 desktop, so
       a generous rootMargin fires at page load on every viewport — the first cut used 1200px
       and deferred nothing at all. The distance also varies by product, so any fixed margin
       big enough to be useful is one short product away from firing at load again.

       So the trigger is engagement, not geometry alone: load when the section is within
       LEAD_PX AND the shopper has actually done something (scroll, tap, key, wheel), or the
       moment the section is genuinely on screen — a short page where the reviews are visible
       without scrolling must still fill itself. Lighthouse does neither, which is where the
       measured win comes from; for a real shopper this moves the 215KB off page load rather
       than avoiding it. */
    var LEAD_PX = 600;
    var engaged = false;

    function check() {
      var r = observed.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var onScreen = r.top < vh && r.bottom > 0;
      if (onScreen || (engaged && r.top < vh + LEAD_PX)) { load(); return true; }
      return false;
    }

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        if (check()) io.disconnect();
        return;
      }
    }, { rootMargin: LEAD_PX + 'px 0px' });
    io.observe(observed);

    function engage() {
      if (engaged) return;
      engaged = true;
      if (check()) io.disconnect();
    }
    ['scroll', 'pointerdown', 'keydown', 'wheel', 'touchstart'].forEach(function (ev) {
      window.addEventListener(ev, engage, { passive: true, once: true });
    });

    window.addEventListener('hashchange', function () {
      if (wantsReviewsNow()) load();
    });
    // Anything pointing at the reviews anchor should load it on click, before the scroll
    // even starts — the sticky CTA's rating link is the common case.
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href*="#reviews"], [data-scroll-reviews]');
      if (a) load();
    }, true);
  }

  window.WPReviewsOnDemand = { load: load };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
